import { BehaviorSubject } from "rxjs";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

type InstallPlatform = "chromium" | "ios-safari" | "generic";

interface InstallPromptState {
  installed: boolean;
  platform: InstallPlatform;
  canPrompt: boolean;
}

function detectInstalled(): boolean {
  if (typeof window === "undefined") return false;
  const mq = window.matchMedia?.("(display-mode: standalone)");
  if (mq?.matches) return true;
  return (
    (window.navigator as Navigator & { standalone?: boolean }).standalone ===
    true
  );
}

function detectPlatform(canPrompt: boolean): InstallPlatform {
  if (canPrompt) return "chromium";
  if (typeof navigator === "undefined") return "generic";
  const ua = navigator.userAgent;
  const isIos = /iPhone|iPad|iPod/.test(ua);
  if (isIos) return "ios-safari";
  return "generic";
}

let storedPrompt: BeforeInstallPromptEvent | null = null;

const state$ = new BehaviorSubject<InstallPromptState>({
  installed: detectInstalled(),
  platform: detectPlatform(false),
  canPrompt: false,
});

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    storedPrompt = event as BeforeInstallPromptEvent;
    state$.next({
      installed: detectInstalled(),
      platform: "chromium",
      canPrompt: true,
    });
  });

  window.addEventListener("appinstalled", () => {
    storedPrompt = null;
    state$.next({
      installed: true,
      platform: detectPlatform(false),
      canPrompt: false,
    });
  });

  const mq = window.matchMedia?.("(display-mode: standalone)");
  mq?.addEventListener?.("change", (e) => {
    state$.next({ ...state$.getValue(), installed: e.matches });
  });
}

export const installPrompt = {
  state$: state$.asObservable(),

  get snapshot(): InstallPromptState {
    return state$.getValue();
  },

  async prompt(): Promise<"accepted" | "dismissed" | "unavailable"> {
    if (!storedPrompt) return "unavailable";
    await storedPrompt.prompt();
    const choice = await storedPrompt.userChoice;
    storedPrompt = null;
    state$.next({ ...state$.getValue(), canPrompt: false });
    return choice.outcome;
  },
};
